import { type PhoneCountry, countryName } from './phoneCountries'

/** A country and the name this locale gives it — what a row draws. */
export interface NamedCountry {
  country: PhoneCountry
  name: string
}

/** One initial's worth of the list, under the heading that names it. */
export interface CountrySection {
  /** The heading, and the label the index rail scrubs to. */
  key: string
  data: NamedCountry[]
}

/** Heads everything no letter does — digits, punctuation — and reads last. */
export const OTHER_SECTION_KEY = '#'

/**
 * The countries as the design groups them (2184:14277): named in the reading
 * locale, ordered by that locale's collation, and cut into sections at the
 * initial.
 *
 * Grouped on the *localised* name rather than the table's English, so a
 * Japanese reader's 日本 is not filed under J, and sorted by a collator rather
 * than by code unit, so Åland lands beside Albania instead of past Zimbabwe.
 */
export function countrySections(
  countries: readonly PhoneCountry[],
  locale: string,
): CountrySection[] {
  const named = countries.map((country) => ({
    country,
    name: countryName(country, locale),
  }))
  const compare = collator(locale)
  named.sort((a, b) => compare(a.name, b.name))

  // Insertion order is collation order, so the sections come out sorted.
  const groups = new Map<string, NamedCountry[]>()
  for (const entry of named) {
    const key = initial(entry.name)
    const held = groups.get(key)
    if (held === undefined) {
      groups.set(key, [entry])
    } else {
      held.push(entry)
    }
  }

  const sections = [...groups].map(([key, data]) => ({ key, data }))
  // Most collations sort punctuation and digits ahead of letters; the design
  // reads that group last, the way a contacts index does.
  const other = sections.findIndex(({ key }) => key === OTHER_SECTION_KEY)
  if (other >= 0) {
    sections.push(...sections.splice(other, 1))
  }
  return sections
}

/**
 * The letter a name files under: its first character, upper-cased and stripped
 * of its accent, so Åland and Albania share a heading.
 */
function initial(name: string): string {
  const [first] = [...stripMarks(name)]
  if (first === undefined) {
    return OTHER_SECTION_KEY
  }
  const upper = first.toUpperCase()
  return LETTER.test(upper) ? upper : OTHER_SECTION_KEY
}

const LETTER = /\p{L}/u
const COMBINING_MARK = /\p{M}/gu

function stripMarks(name: string): string {
  return name.normalize('NFD').replace(COMBINING_MARK, '')
}

// One per locale: constructing a collator is expensive, and the list would
// otherwise build one per comparison.
const COLLATORS = new Map<string, (a: string, b: string) => number>()

function collator(locale: string): (a: string, b: string) => number {
  const held = COLLATORS.get(locale)
  if (held !== undefined) {
    return held
  }
  const compare = buildCollator(locale)
  COLLATORS.set(locale, compare)
  return compare
}

function buildCollator(locale: string): (a: string, b: string) => number {
  try {
    return new Intl.Collator(locale).compare
  } catch {
    // A runtime without ICU still has to order the list somehow.
    return byCodeUnit
  }
}

function byCodeUnit(a: string, b: string): number {
  if (a < b) {
    return -1
  }
  return a > b ? 1 : 0
}
